import './index.css'

import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Link,
  NavigateFunction,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { bindActionCreators } from 'redux'

import api from '@disco/api/server'
import {
  ALBUMS,
  CUSTOM_FORMAT_SETTINGS,
  PASSWORD_PROTECTION,
  TRACK_NESTING,
  URL_EXPIRY_DATE,
  WATERMARKING,
} from '@disco/constants/FeatureTypes'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { CUSTOM_CHANNEL, TERRITORY } from '@disco/constants/GroupTypes'
import {
  STORAGE_DISABLED_NOTIFICATION,
  STORAGE_FULL_NOTIFICATION,
} from '@disco/constants/notifications'
import { PROCESSING } from '@disco/constants/WatermarkStatusesTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { loadPlaylistZipUrls } from '@disco/state-management/actions/playlist-actions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import * as ShortlistActions from '@disco/state-management/actions/ShortlistActions'
import {
  isFeatureEnabled,
  makeGetFeatureData,
  makeIsFeatureEnabled,
} from '@disco/state-management/selectors/business'
import {
  getGroups,
  getGroupsWithNesting,
  getPersonalLibrary,
  getTeamLibrary,
} from '@disco/state-management/selectors/groups'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import captureError, { parseServerError } from '@disco/utils/captureError'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import type { WatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import { getWatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import { pushUINotification } from '@disco/utils/notifications'
import { isQuotaExceededError } from 'utils/local-storage'
import {
  hasShortlistContainMainTrack,
  isFormatsTabDisabled,
} from '@disco/utils/shortlist'

import type { Group, GroupWithNesting } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { ErrorsObject } from 'types/server-error'
import type { Shortlist } from 'types/shortlist'
import type { User } from 'types/user'

import LoadingIndicator from 'components/common/copy-metadata-track/loading-indicator'
import TagSelector from 'components/common/tag-selector'
import HelpTooltip from 'components/common/tooltip'
import SecurityTab from 'components/modals/playlist/share/security-tab'
import Accordion from 'components/ui/accordion'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Collapsible from 'components/ui/collapsible'
import DatePicker from 'components/ui/date-picker'
import Flex from 'components/ui/flex'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'
import PricingBadge from 'components/ui/pricing-badge'
import PricingPopover from 'components/ui/pricing-popover'
import Stack from 'components/ui/stack'
import SwitchContainer from 'components/ui/switch/container'
import TextField from 'components/ui/text-field'

import type { RootState } from 'store/configureStore'
import { ShortlistGroupsInput } from './shortlist-groups-input'

const EN_SPACE = ' '

// TODO: Refactor component for useLocalStorage hook
const getLocalStorageValue = key => {
  const item = window.localStorage.getItem(key)
  return item ? JSON.parse(item) : undefined
}

function mapStateToProps(
  state: RootState,
  { shortlistId, playlistId }: { shortlistId: string; playlistId?: string },
) {
  const shortlist = getShortlistById(state, {
    id: shortlistId,
  })

  const getFeatureData = makeGetFeatureData()
  const playlist = getPlaylist(state, {
    id: playlistId ? parseInt(playlistId, 10) : undefined,
  })
  const { isFeatureEnabled: passwordProtectionEnabled } = getFeatureData(
    state,
    {
      featureName: PASSWORD_PROTECTION,
    },
  )
  const { isFeatureEnabled: customFormatsEnabled, isTrial } = getFeatureData(
    state,
    {
      featureName: CUSTOM_FORMAT_SETTINGS,
    },
  )
  const shareSecurityEnabled = [
    {
      isFeatureEnabled: passwordProtectionEnabled,
    },
    {
      isFeatureEnabled: getFeatureData(state, {
        featureName: URL_EXPIRY_DATE,
      }),
    },
  ].some(({ isFeatureEnabled }) => isFeatureEnabled)
  return {
    shortlist,
    playlist,
    user: state.user,
    themes: state.themes,
    groups: getGroups(state),
    groupsWithNesting: getGroupsWithNesting(state),
    tracks: state.tracks,
    watermarkingEnabled: getFeatureData(state, {
      featureName: WATERMARKING,
    }).isFeatureEnabled,
    watermarkingQuota: getWatermarkingQuota(state),
    albumsEnabled: getFeatureData(state, {
      featureName: ALBUMS,
    }).isFeatureEnabled,
    defaultGroup: getTeamLibrary(state),
    myLibraryGroup: getPersonalLibrary(state),
    nestingEnabled: isFeatureEnabled(state, {
      featureName: TRACK_NESTING,
    }),
    hasNestedTracks: hasShortlistContainMainTrack(shortlist),
    shareSecurityEnabled,
    customFormatsEnabled,
    isTrial,
    ...state.forms.playlistShareForm,
    loadBatch,
    sendGenericError,
    loadPlaylistZipUrls,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    { ...ShortlistActions, sendMixpanelEvent },
    dispatch,
  )
}

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type from Redux
type PropsFromRedux = ConnectedProps<typeof connector>

// Own props interface
interface OwnProps {
  navigate: NavigateFunction
  params: {
    shortlistId: string
  }
  shortlistId: string
  playlistId?: string
}

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps
const OPEN_SECTIONS_LOCAL_STORAGE_KEY = 'disco_ShortlistSave_openSections'

class ShortlistSave extends React.Component<
  Props,
  {
    showAddToChannel: boolean
    showMoreOptions: boolean
    showPlaylistOptions: boolean
    showPlaylistTags: boolean
    showContentSettings: boolean
    showBrowseSettings: boolean
    showSecuritySettings: boolean
    showNestingAlert: boolean
    playlistBaseObj: Record<string, any> | null | undefined
    playlistBaseObjErrorMessage: ErrorsObject | null | undefined
  }
> {
  state = {
    showAddToChannel: false,
    showMoreOptions: false,
    showPlaylistOptions: true,
    showPlaylistTags: false,
    showNestingAlert: false,
    playlistBaseObj: null,
    showContentSettings: true,
    showSecuritySettings: false,
    showBrowseSettings: false,
    playlistBaseObjErrorMessage: null,
  }

  componentDidMount() {
    this._checkForAlbum()

    if (this.props.shortlist.playlist_id) {
      this._fetchPlaylistSecurity(this.props)
    }

    const openSections = getLocalStorageValue(OPEN_SECTIONS_LOCAL_STORAGE_KEY)

    if (openSections) {
      const {
        showAddToChannel,
        showMoreOptions,
        showPlaylistOptions,
        showPlaylistTags,
      } = openSections
      this.setState({
        showAddToChannel: Boolean(showAddToChannel),
        showMoreOptions: Boolean(showMoreOptions),
        showPlaylistOptions: Boolean(showPlaylistOptions),
        showPlaylistTags: Boolean(showPlaylistTags),
      })
    }

    if (!this.props.shortlist) {
      this.props.navigate('/')
      return
    }

    // for new shortlists try to assign default theme
    const { shortlist } = this.props
    // don't try to set default themes to previously saved playlists or
    // playlists that have theme already
    if (shortlist.playlist_id || shortlist.theme) return

    // if theme set explicitly to false, we shouldn't use default theme
    if (shortlist.theme !== false) {
      const { themes } = this.props.themes
      const defaultTheme = themes.find(item => item.default)
      if (defaultTheme) this._handleThemeUpdate(defaultTheme)
    }
  }

  componentWillUnmount() {
    const {
      showAddToChannel,
      showMoreOptions,
      showPlaylistOptions,
      showPlaylistTags,
      showSecuritySettings,
      showBrowseSettings,
      showContentSettings,
    } = this.state

    this._setLocalStorageValue(OPEN_SECTIONS_LOCAL_STORAGE_KEY, {
      showAddToChannel,
      showMoreOptions,
      showPlaylistOptions,
      showPlaylistTags,
      showSecuritySettings,
      showBrowseSettings,
      showContentSettings,
    })
  }

  _setLocalStorageValue = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      pushUINotification(
        isQuotaExceededError(error)
          ? STORAGE_FULL_NOTIFICATION
          : STORAGE_DISABLED_NOTIFICATION,
      )
      captureError(error)
    }
  }
  _closeModal = () => this.props.navigate('/')

  _getSelectedGroup() {
    const { shortlist, defaultGroup, myLibraryGroup } = this.props
    const groups = shortlist.groups.filter(group => group)
    const selectedGroup: Group | null | undefined =
      (defaultGroup &&
        groups.find(g => g.group_type === defaultGroup.group_type)) ||
      (myLibraryGroup &&
        groups.find(g => g.group_type === myLibraryGroup.group_type))
    return {
      selectedGroup,
      defaultGroup,
      myLibraryGroup,
    }
  }

  _renderVisibility() {
    const { params, shortlist } = this.props
    const groups = shortlist.groups.filter(group => group)

    const { selectedGroup, defaultGroup, myLibraryGroup } =
      this._getSelectedGroup()

    return (
      <SwitchContainer>
        <SwitchContainer.SwitchLabel label="Viewable only by me">
          <HelpTooltip
            id="tooltip_save_playlist_modal_viewable"
            base={
              <Icon
                type="info-outline"
                color="#42526E"
                width={15}
                height={15}
              />
            }
            noCssWrapper
          />
        </SwitchContainer.SwitchLabel>
        <SwitchContainer.Switch
          value={selectedGroup?.id === myLibraryGroup?.id}
          onChange={(checked: boolean) => {
            const types = myLibraryGroup
              ? [defaultGroup.group_type, myLibraryGroup.group_type]
              : [defaultGroup.group_type]
            let nextGroups = [...groups]
            nextGroups = nextGroups.filter(g => !types.includes(g.group_type))
            nextGroups = nextGroups.concat(
              !myLibraryGroup || selectedGroup?.id === myLibraryGroup.id
                ? defaultGroup
                : myLibraryGroup,
            )
            this.props.sendMixpanelEvent(
              checked
                ? 'Playlist Info tab - Viewable only by me on'
                : 'Playlist Info tab - Viewable only by me off',
            )
            this.props.updateShortlist(params.shortlistId, {
              groups: nextGroups,
            })
          }}
        />
      </SwitchContainer>
    )
  }

  _checkForAlbum = () => {
    const { shortlist, albumsEnabled } = this.props
    if (shortlist.playlist_id || !albumsEnabled) return
    const MIN_AUDIO_FOR_ALBUM = 2
    const audioFiles = shortlist.items.filter(({ track }) => {
      const type = getFileTypeByExt(track.ext)
      return type === AUDIO_FILE
    })
    const albums = []
    const artists = []
    audioFiles.forEach(({ track }) => {
      if (!albums.includes(track.album)) {
        albums.push(track.album)
      }

      if (!artists.includes(track.artist)) {
        artists.push(track.artist)
      }
    })
    const albumCondition = albums.length === 1 && Boolean(albums[0])
    const artistCondition = artists.length === 1 && Boolean(artists[0])
    // • 2+ audio files
    // • all audio files in playlist have same album/artist
    const countCondition = audioFiles.length >= MIN_AUDIO_FOR_ALBUM
    const isAlbum = albumCondition && artistCondition && countCondition

    this._handleAlbumToggle(isAlbum)
  }

  async _fetchPlaylistSecurity(props) {
    let obj = {}

    try {
      obj = await api.getPlaylistBaseObj(props.shortlist.playlist_id)
      this.setState({
        playlistBaseObj: obj,
      })
    } catch (error) {
      captureError(error, { description: 'Error in _fetchPlaylistSecurity()' })
      this.setState({
        playlistBaseObjErrorMessage: error,
      })
    }
  }

  _onTagsSelect = tags => {
    this.props.updateShortlist(this.props.params.shortlistId, {
      tags: tags.map(t => t.value),
    })

    if (tags.length > this.props.shortlist.tags.length) {
      this.props.sendMixpanelEvent('Playlist Info tab - Playlist tag added')
    } else {
      this.props.sendMixpanelEvent('Playlist Info tab - Playlist tag removed')
    }
  }
  _handleClientToggle = (checked: boolean) => {
    this.props.sendMixpanelEvent(
      checked
        ? 'Playlist Info tab - Client version on'
        : 'Playlist Info tab - Client version off',
    )
    this.props.updateShortlist(this.props.params.shortlistId, {
      is_client_version: checked,
    })
  }
  _handleAlbumToggle = (checked: boolean) => {
    this.props.sendMixpanelEvent(
      checked
        ? 'Playlist Info tab - Album on'
        : 'Playlist Info tab - Album off',
    )
    this.props.updateShortlist(this.props.params.shortlistId, {
      playlist_type: checked ? 'album' : 'playlist',
    })
  }
  _handleReleaseDateChange = (date?: string) => {
    this.props.sendMixpanelEvent('Playlist Info tab - Update release date')
    this.props.updateShortlist(this.props.params.shortlistId, {
      release_date: date,
    })
  }
  _toggleState = (key: string) => {
    this.setState(prevState => ({ ...prevState, [key]: !prevState[key] }))
  }

  _renderPricingPopover(props: {
    children: React.ReactNode
    description: React.ReactNode
    messageClassName?: string
  }) {
    if (props.description) {
      return <PricingPopover {...props} />
    }

    return props.children
  }

  _renderPlaylistOptions() {
    const {
      albumsEnabled,
      shortlist,
      playlist,
      watermarkingEnabled,
      defaultGroup,
      myLibraryGroup,
      nestingEnabled,
      watermarkingQuota,
      customFormatsEnabled,
      isTrial,
      tracks,
    } = this.props
    const customFormatsNotApplicable =
      isFormatsTabDisabled(shortlist, tracks) &&
      !hasShortlistContainMainTrack(shortlist)
    const {
      groups,
      is_client_version,
      download_links,
      show_lyrics,
      playlist_type,
      release_date,
      share_stems,
      watermark,
      watermarking_status,
      include_nested_track_versions,
    } = shortlist
    const MAX_SHOW_OPTIONS = watermarkingEnabled ? 4 : 3
    const starredGroupSwitchDisabled =
      !myLibraryGroup ||
      (!groups.find(g => g.group_type === defaultGroup?.group_type) &&
        !!groups.find(g => g.id === myLibraryGroup?.id))
    const watermarkingProcessing = watermarking_status === PROCESSING
    const watermarkingDisabled =
      !watermarkingEnabled ||
      watermarkingProcessing ||
      (!watermark && watermarkingQuota.overQuota)
    let watermarkingTooltip = null

    if (watermarkingProcessing) {
      // TODO: remove code duplication - SecurityTab already has it
      watermarkingTooltip = (
        <>
          This playlist is currently being watermarked, so this setting can’t be
          changed.
          <br /> You’ll be able to change this setting once the watermarking
          completes.
        </>
      )
    }

    if (watermarkingQuota.overQuota) {
      // TODO: remove code duplication - SecurityTab already has it
      watermarkingTooltip = (
        <>
          You’ve reached your watermarking quota. <br /> To watermark more
          playlists, <InlineLink to="/settings/billing">upgrade</InlineLink> for
          <br /> more watermarking quota, or remove <br />
          watermarking from another playlist.
        </>
      )
    }

    if (!watermarkingEnabled) {
      // TODO: remove code duplication - SecurityTab already has it
      watermarkingTooltip = (
        <>
          <div>Watermark your tracks for extra security.</div>
          <Link to="/watermarking" className="ShortlistSave_Link">
            Find out more
          </Link>
        </>
      )
    }

    const formatsForm = (
      <Stack space="xsmall">
        <SwitchContainer
          key="Optimized only"
          disabled={customFormatsNotApplicable}
        >
          <SwitchContainer.SwitchLabel
            label={
              <>
                Optimized only{EN_SPACE}
                {customFormatsEnabled ? null : <PricingBadge title="Upgrade" />}
              </>
            }
          >
            <HelpTooltip
              id="tooltip_formats_share_mode"
              base={
                <Icon
                  type="info-outline"
                  color="#42526E"
                  width={15}
                  height={15}
                />
              }
              noCssWrapper
            />
          </SwitchContainer.SwitchLabel>
          <SwitchContainer.Switch
            value={shortlist?.share_mode === 'optimized_only'}
            onChange={this._onShareModeChange}
            disabled={customFormatsNotApplicable || !customFormatsEnabled}
          />
        </SwitchContainer>
        <SwitchContainer
          key="Include original formats"
          disabled={customFormatsNotApplicable}
        >
          <SwitchContainer.SwitchLabel
            label={
              <>
                Include original formats{EN_SPACE}
                {customFormatsEnabled ? null : <PricingBadge title="Upgrade" />}
              </>
            }
          >
            <HelpTooltip
              id="tooltip_formats_zip_mode"
              base={
                <Icon
                  type="info-outline"
                  color="#42526E"
                  width={15}
                  height={15}
                />
              }
              noCssWrapper
            />
          </SwitchContainer.SwitchLabel>
          <SwitchContainer.Switch
            value={shortlist?.default_zip_mode === 'original_else_optimized'}
            onChange={this._onZipModeChange}
            disabled={customFormatsNotApplicable || !customFormatsEnabled}
          />
        </SwitchContainer>
      </Stack>
    )
    const content = [
      <Accordion
        key="Content Settings"
        expanded={this.state.showContentSettings}
        title={<h2 className="ShortlistSave_FormTitle ">Content Settings</h2>}
        onTitleClick={() => this._toggleState('showContentSettings')}
      >
        <Stack space="xsmall">
          <SwitchContainer key="Streaming only">
            <SwitchContainer.SwitchLabel label="Streaming only" />
            <SwitchContainer.Switch
              value={!download_links}
              onChange={this._handleStreamOnlyUpdate}
            />
          </SwitchContainer>
          {nestingEnabled && (
            <>
              <Stack space="xsmall">
                <SwitchContainer key="Include nested track versions">
                  <SwitchContainer.SwitchLabel label="Include nested track versions">
                    <HelpTooltip
                      id="tooltip_include_nested_versions_modal_viewable"
                      base={
                        <Icon
                          type="info-outline"
                          color="#42526E"
                          width={15}
                          height={15}
                        />
                      }
                      noCssWrapper
                    />
                  </SwitchContainer.SwitchLabel>
                  <SwitchContainer.Switch
                    value={include_nested_track_versions}
                    onChange={this._onShareVersionsModeChange}
                  />
                </SwitchContainer>
                <SwitchContainer key="Share stems">
                  <SwitchContainer.SwitchLabel label="Share stems">
                    <HelpTooltip
                      id="tooltip_include_stems_modal_viewable"
                      base={
                        <Icon
                          type="info-outline"
                          color="#42526E"
                          width={15}
                          height={15}
                        />
                      }
                      noCssWrapper
                    />
                  </SwitchContainer.SwitchLabel>
                  <SwitchContainer.Switch
                    value={!!share_stems}
                    onChange={this._onShareStemsModeChange}
                  />
                </SwitchContainer>
              </Stack>
            </>
          )}
          {this.state.showNestingAlert && (
            <ModalDialog
              title="Nested tracks found in this playlist."
              primaryAction={{
                caption: 'OK',
                onClick: () => {
                  this.props.sendMixpanelEvent(
                    'include_nested_track_versions',
                    { value: !shortlist.include_nested_track_versions },
                  )
                  this.setState({
                    showNestingAlert: false,
                  })
                },
              }}
              onCancel={() =>
                this.setState({
                  showNestingAlert: false,
                })
              }
              status="warning"
              width="large"
            >
              Turning off nesting for this playlist will cause all nested
              versions to be no longer shared. Nested tracks that were
              previously in this playlist will be removed.
            </ModalDialog>
          )}
          <SwitchContainer key="Show lyrics">
            <SwitchContainer.SwitchLabel label="Show lyrics" />
            <SwitchContainer.Switch
              value={!!show_lyrics}
              onChange={this._handleCheckboxUpdate}
            />
          </SwitchContainer>
        </Stack>
      </Accordion>,
      <div key="Security Settings">
        <Accordion
          expanded={this.state.showSecuritySettings}
          title={
            <h2 className="ShortlistSave_FormTitle ">Security Settings</h2>
          }
          onTitleClick={() => this._toggleState('showSecuritySettings')}
        >
          <Stack space="xsmall">
            <SwitchContainer
              key="Watermark playlist"
              disabled={!watermarkingEnabled}
            >
              <SwitchContainer.SwitchLabel label="Watermark playlist" />
              <Flex>
                {this._renderPricingPopover({
                  description: watermarkingTooltip,
                  children: (
                    <SwitchContainer.Switch
                      value={watermark}
                      onChange={this._handleWatermarkUpdate}
                      disabled={watermarkingDisabled}
                    />
                  ),
                })}
              </Flex>
            </SwitchContainer>
            {shortlist.playlist_id ? (
              <>
                {this.state.playlistBaseObj ? (
                  <>
                    <SecurityTab
                      playlistSettings={true}
                      playlistBaseObj={this.state.playlistBaseObj}
                      setPlaylistBaseObj={changes => {
                        this.setState({
                          playlistBaseObj: {
                            ...this.state.playlistBaseObj,
                            ...changes,
                          },
                        })

                        if (playlist) {
                          this.props.loadBatch({ ...playlist, ...changes })
                        }
                      }}
                      shareSecurityEnabled={this.props.shareSecurityEnabled}
                      user={this.props.user as unknown as User}
                      pushUINotification={pushUINotification}
                      sendMixpanelEvent={this.props.sendMixpanelEvent}
                      watermarkingEnabled={watermarkingEnabled}
                      watermarkingQuota={watermarkingQuota}
                      isTrial={isTrial}
                      shortlist={shortlist}
                    />
                    {formatsForm}
                  </>
                ) : (
                  <>
                    {this.state.playlistBaseObjErrorMessage ? (
                      <Alert icon="warning" status="warning">
                        {parseServerError(
                          this.state.playlistBaseObjErrorMessage,
                        )}
                      </Alert>
                    ) : (
                      <div className="Security_LoadingIndicator">
                        <LoadingIndicator size="small" />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              formatsForm
            )}
          </Stack>
        </Accordion>
      </div>,
      <div key="Browse Settings">
        <Accordion
          expanded={this.state.showBrowseSettings}
          title={<h2 className="ShortlistSave_FormTitle">Browse Settings</h2>}
          onTitleClick={() => this._toggleState('showBrowseSettings')}
        >
          <Stack space="xsmall">
            <SwitchContainer key="Client Version">
              <SwitchContainer.SwitchLabel label="Client Version">
                <HelpTooltip
                  id="tooltip_save_playlist_modal_client"
                  base={
                    <Icon
                      type="info-outline"
                      color="#42526E"
                      width={15}
                      height={15}
                    />
                  }
                  noCssWrapper
                />
              </SwitchContainer.SwitchLabel>
              <SwitchContainer.Switch
                value={!!is_client_version}
                onChange={this._handleClientToggle}
              />
            </SwitchContainer>
            {this._renderVisibility()}
            {albumsEnabled && (
              <>
                <SwitchContainer key="Album">
                  <SwitchContainer.SwitchLabel label="Album" />
                  <SwitchContainer.Switch
                    value={playlist_type === 'album'}
                    onChange={this._handleAlbumToggle}
                  />
                </SwitchContainer>
                <Collapsible open={playlist_type === 'album'}>
                  <div className="ShortlistSave_ReleaseDateContainer">
                    <Label caption="Release date" />
                    <DatePicker
                      value={release_date}
                      onChange={this._handleReleaseDateChange}
                    />
                  </div>
                </Collapsible>
              </>
            )}
            <SwitchContainer
              disabled={starredGroupSwitchDisabled}
              key="Add to my starred lists"
            >
              <SwitchContainer.SwitchLabel label="Add to my starred lists" />
              <SwitchContainer.Switch
                disabled={starredGroupSwitchDisabled}
                value={
                  !!myLibraryGroup &&
                  !!groups.find(g => g.id === myLibraryGroup.id)
                }
                onChange={(checked: boolean) => {
                  const types = myLibraryGroup
                    ? [myLibraryGroup.group_type]
                    : []
                  const filteredGroups = groups.filter(
                    g => !types.includes(g.group_type),
                  )
                  this.props.sendMixpanelEvent(
                    checked
                      ? 'Playlist Info tab - Add to my starred lists on'
                      : 'Playlist Info tab - Add to my starred lists off',
                  )
                  this.props.updateShortlist(this.props.params.shortlistId, {
                    groups:
                      !myLibraryGroup ||
                      groups.find(g => g.id === myLibraryGroup?.id)
                        ? filteredGroups
                        : [...filteredGroups, myLibraryGroup],
                  })
                }}
              />
            </SwitchContainer>
          </Stack>
        </Accordion>
      </div>,
    ].filter(Boolean)
    const hideContent = content.splice(MAX_SHOW_OPTIONS)
    return (
      <>
        <Stack space="xsmall">
          {content.splice(0, MAX_SHOW_OPTIONS)}
          {Boolean(hideContent.length) && (
            <>
              <Collapsible open={this.state.showMoreOptions} scrollIntoView>
                {hideContent}
              </Collapsible>
              <Button
                appearance="inline-link"
                caption={
                  this.state.showMoreOptions
                    ? 'Show less options'
                    : 'Show more options'
                }
                onClick={() => this._toggleState('showMoreOptions')}
              />
            </>
          )}
        </Stack>
      </>
    )
  }

  _renderForm() {
    const { shortlist } = this.props
    const { title, tags } = shortlist
    return (
      <div className="Form" id="ShortlistSaveForm">
        <Stack space="small">
          <TextField
            compact={false}
            label="Name"
            defaultValue={title}
            onChange={this._handleTitleUpdate}
          />

          <div> {this._renderPlaylistOptions()}</div>
          <div>
            <h2 className="ShortlistSave_FormTitle">Playlist tags</h2>
            <TagSelector
              onChange={this._onTagsSelect}
              selectedTags={tags}
              placeholder="Select tags, or type to create new tags"
              clearable
              allowCreate
              enableUp
            />
          </div>
          <div>
            <h2 className="ShortlistSave_FormTitle">Add to channel</h2>
            <ShortlistGroupsInput
              defaultGroup={this.props.defaultGroup}
              myLibraryGroup={this.props.myLibraryGroup}
              groupsWithNesting={this.props.groupsWithNesting}
              user={this.props.user}
              shortlistId={this.props.params.shortlistId}
              shortlist={shortlist}
              allGroups={this.props.groups}
              sendMixpanelEvent={this.props.sendMixpanelEvent}
              updateShortlist={this.props.updateShortlist}
            />
          </div>
        </Stack>
      </div>
    )
  }

  _handleFormatSubmit = update => {
    this.props.updateShortlist(this.props.params.shortlistId, update)
  }
  _handleTitleUpdate = evt => {
    this._handleFormatSubmit({
      title: evt.target.value,
    })
  }
  _handleWatermarkUpdate = (checked: boolean) => {
    this.props.sendMixpanelEvent(
      checked
        ? 'Playlist Info tab - Watermarking on'
        : 'Playlist Info tab - Watermarking off',
    )

    this._handleFormatSubmit({
      watermark: checked,
    })
  }
  _onZipModeChange = (checked: boolean) => {
    const { shortlist } = this.props
    this.props.sendMixpanelEvent(
      checked
        ? 'Formats tab - Include original formats on'
        : 'Formats tab - Include original formats off',
    )

    if (shortlist.default_zip_mode !== 'optimized') {
      this._handleFormatSubmit({
        default_zip_mode: 'optimized',
      })
    } else {
      this._handleFormatSubmit({
        default_zip_mode: 'original_else_optimized',
        share_mode: 'standard',
      })
    }
  }
  _onShareModeChange = (checked: boolean) => {
    const { shortlist } = this.props
    this.props.sendMixpanelEvent(
      checked
        ? 'Formats tab - Send only optimized formats on'
        : 'Formats tab - Send only optimized formats off',
    )

    if (shortlist.share_mode !== 'standard') {
      this._handleFormatSubmit({
        share_mode: 'standard',
      })
    } else {
      this._handleFormatSubmit({
        share_mode: 'optimized_only',
        default_zip_mode: 'optimized',
      })
    }
  }
  _handleStreamOnlyUpdate = (checked: boolean) => {
    this.props.sendMixpanelEvent(
      checked
        ? 'Playlist Info tab - Streaming only on'
        : 'Playlist Info tab - Streaming only off',
    )

    this._handleFormatSubmit({
      download_links: !checked,
    })
  }
  _onShareVersionsModeChange = (checked: boolean) => {
    const { hasNestedTracks, nestingEnabled } = this.props

    this._handleFormatSubmit({
      include_nested_track_versions: checked,
    })

    if (checked) {
      this.props.normalizeNestedTracksInShortlists()
    }

    const showTurningOffNestingAlert =
      !checked && hasNestedTracks && nestingEnabled

    if (showTurningOffNestingAlert) {
      this.setState({
        showNestingAlert: true,
      })
      return
    }
  }
  _onShareStemsModeChange = (checked: boolean) => {
    this.props.sendMixpanelEvent(
      checked
        ? 'Playlist Info tab - Share stems mode on'
        : 'Playlist Info tab - Share stems mode off',
    )

    this._handleFormatSubmit({
      share_stems: checked,
    })

    if (checked) {
      this.props.normalizeNestedTracksInShortlists()
    }
  }
  _handleCheckboxUpdate = (checked: boolean) => {
    this.props.sendMixpanelEvent(
      checked
        ? 'Playlist Info tab - Show lyrics on'
        : 'Playlist Info tab - Show lyrics off',
    )

    this._handleFormatSubmit({
      show_lyrics: checked,
    })
  }

  _handleThemeUpdate(theme) {
    this.props.updateShortlist(this.props.params.shortlistId, {
      theme: theme,
    })
  }

  _deleteGroupFromShortlist = groupId => {
    const { shortlist } = this.props
    const index = shortlist.groups.findIndex(g => g.id === groupId)

    if (index === -1) {
      throw new Error(`Failed to remove group ${groupId}`)
    }

    const groups = [
      ...shortlist.groups.slice(0, index),
      ...shortlist.groups.slice(index + 1),
    ]
    this.props.updateShortlist(this.props.params.shortlistId, {
      groups: groups,
    })
  }

  render() {
    if (!this.props.shortlist) return null
    return this._renderForm()
  }
}

const ConnectedShortlistSave = connector(ShortlistSave)

function ShortlistSaveWithNavigation(props: any) {
  const navigate = useNavigate()
  const { shortlistId } = useParams<{ shortlistId: string }>()
  return (
    <ConnectedShortlistSave
      {...props}
      navigate={navigate}
      params={{ shortlistId }}
      shortlistId={shortlistId}
    />
  )
}

export { ShortlistSave }
export default ShortlistSaveWithNavigation
